import { FormProvider, useForm } from "react-hook-form";
import { useEffect, useState, useMemo, useCallback } from "react";
import { InferType, object, number, boolean } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Feature, FeatureCollection, MultiPolygon, Point } from "geojson";
import { PlotType } from "@/types/enums";
import { SlicePlotFeature } from "@/types/plot";
import { IrrigationSystem } from "@/types/irrigationSystem";
import { Button } from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faRotateLeft,
    faRotateRight,
    faPlus,
    faMinus,
    faShuffle,
} from "@fortawesome/free-solid-svg-icons";
import { incrementDegree, decrementDegree } from "@/lib/utils/sector";
import NumberInput from "../inputs/NumberInput";
import { getSectorGeometry } from "@/lib/utils/sector";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useMap from "@/lib/map/useMap";
import useAuth from "@/lib/auth/useAuth";
import {
    addUpdateGeojsonSource,
    addUpdateGeojsonLayers,
    removeLayersIfExists,
    removeSourcesIfExists,
    moveLayersToTop,
} from "@/lib/utils/map";
import {
    PLOT_HIGHLIGHT_SOURCE,
    PLOT_HIGHLIGHT_FILL_LAYER,
    PLOT_HIGHLIGHT_LINE_LAYER,
} from "@/lib/map/layers";
import * as turf from "@turf/turf";
import { MapMouseEvent } from "mapbox-gl";
import { useTranslation } from "react-i18next";
import useFeatureFlags from "@/lib/featureFlags/useFeatureFlags";

const schema = object().shape({
    userIsSentinel: boolean().required(),
    canary_offset: number()
        .required()
        .default(-30)
        .label("Canary Offset")
        .when("userIsSentinel", {
            is: (val: boolean) => val,
            then: (schema) => schema.max(0),
            otherwise: (schema) => schema.max(-30),
        }),
    reference_offset: number()
        .required()
        .default(30)
        .label("Reference Offset")
        .when("userIsSentinel", {
            is: (val: boolean) => val,
            then: (schema) => schema.min(0),
            otherwise: (schema) => schema.min(30),
        }),
    slice_size: number().required().default(5).label("Slice Size"),
    start_degree: number().required().default(0).label("Start Degree"),
    center_degree: number().required().default(5).label("Center Degree"),
    stop_degree: number().required().default(10).label("Stop Degree"),
    canary_geometry: object().required(),
    reference_geometry: object().required(),
    canary_id: number().nullable(),
    reference_id: number().nullable(),
});
export type SliceForm = InferType<typeof schema>;

function SlicePlotForm({
    placement,
    rxGeometry,
    irrigationSystemId,
    plots,
    onFormChange,
}: {
    placement: "manual" | "automatic";
    rxGeometry: FeatureCollection<MultiPolygon>;
    irrigationSystemId: number;
    plots?: Feature[];
    onFormChange?: (form: SliceForm | SlicePlotFeature[]) => void;
}) {
    const { t } = useTranslation();
    const form = useForm<SliceForm>({
        resolver: yupResolver(schema),
        defaultValues: {
            canary_offset: -30,
            reference_offset: 30,
            slice_size: 5,
        },
        mode: "onChange",
    });
    const [
        startDegree,
        centerDegree,
        stopDegree,
        canaryOffset,
        referenceOffset,
        sliceSize,
    ] = form.watch([
        "start_degree",
        "center_degree",
        "stop_degree",
        "canary_offset",
        "reference_offset",
        "slice_size",
    ]);
    const [firstPlotType, setFirstPlotType] = useState<PlotType>(
        PlotType.Canary,
    );
    const { fieldQuery } = useFieldDashboard() ?? {};
    const { map, mapLoaded } = useMap();
    const { userIsSentinel, user } = useAuth() ?? {};
    const { fullPlotOffsetEnabled } = useFeatureFlags();
    const units = useMemo(() => {
        return user?.unit_preference === "metric" ? "meters" : "feet";
    }, [user?.unit_preference]);
    const irrigationSystem = useMemo(() => {
        return fieldQuery?.data?.irrigation_systems.find(
            (system: IrrigationSystem) => system.id === irrigationSystemId,
        );
    }, [fieldQuery?.data, irrigationSystemId]);

    useEffect(() => {
        if (plots && plots.length > 0) {
            const canary = plots.find(
                (plot) => plot.properties?.plot_type === PlotType.Canary,
            );
            const reference = plots.find(
                (plot) => plot.properties?.plot_type === PlotType.Reference,
            );
            if (!canary || !reference) return;
            const firstPlot =
                canary.properties?.stop_degree ===
                reference.properties?.start_degree
                    ? canary
                    : reference;
            const secondPlot =
                canary.properties?.stop_degree ===
                reference.properties?.start_degree
                    ? reference
                    : canary;
            setFirstPlotType(firstPlot.properties?.plot_type);
            form.reset({
                userIsSentinel: userIsSentinel || fullPlotOffsetEnabled,
                canary_offset: canary.properties?.rate_offset,
                reference_offset: reference.properties?.rate_offset,
                slice_size:
                    canary.properties?.stop_degree -
                    canary.properties?.start_degree,
                start_degree: firstPlot.properties?.start_degree,
                center_degree: firstPlot.properties?.stop_degree,
                stop_degree: secondPlot.properties?.stop_degree,
                canary_geometry: canary.geometry,
                reference_geometry: reference.geometry,
                canary_id: Number(canary.id),
                reference_id: Number(reference.id),
            });
        } else {
            const startDegree = irrigationSystem?.pivot_start_degree ?? 0;
            form.reset({
                userIsSentinel: userIsSentinel || fullPlotOffsetEnabled,
                start_degree: startDegree,
                center_degree: startDegree + 5,
                stop_degree: startDegree + 10,
            });
        }
    }, [plots, irrigationSystem, form, userIsSentinel, fullPlotOffsetEnabled]);

    const [dragActive, setDragActive] = useState<boolean>(false);
    const startDrag = useCallback(() => {
        if (!map || !!dragActive) return;
        setDragActive(true);
        map.getCanvas().style.cursor = "grabbing";
        map.dragPan.disable();
    }, [map, dragActive]);
    const drag = useCallback(
        (e: MapMouseEvent) => {
            if (!map || !dragActive || !irrigationSystem?.pivot_center) return;
            const bearing = turf.bearing(
                turf.point([
                    irrigationSystem.pivot_center.coordinates[0],
                    irrigationSystem.pivot_center.coordinates[1],
                ]),
                turf.point(e.lngLat.toArray()),
            );
            const degree = Math.round(turf.bearingToAzimuth(bearing));
            form.setValue("center_degree", degree);
            form.setValue("start_degree", decrementDegree(degree, sliceSize));
            form.setValue("stop_degree", incrementDegree(degree, sliceSize));
        },
        [map, dragActive, irrigationSystem, form, sliceSize],
    );
    const stopDrag = useCallback(() => {
        if (!map || !dragActive) return;
        setDragActive(false);
        map.getCanvas().style.cursor = "";
        map.dragPan.enable();
    }, [map, dragActive]);

    const makeSliceGeometry = useCallback(
        (
            startDegree: number,
            centerDegree: number,
            stopDegree: number,
            centerPoint: Point,
            length: number,
        ): Feature<MultiPolygon>[] => {
            const plot1 = getSectorGeometry(
                startDegree,
                centerDegree,
                centerPoint,
                length,
                undefined,
                units,
            );
            const plot2 = getSectorGeometry(
                centerDegree,
                stopDegree,
                centerPoint,
                length,
                undefined,
                units,
            );
            return [plot1, plot2];
        },
        [units],
    );

    useEffect(() => {
        if (
            !map ||
            !mapLoaded ||
            !irrigationSystem?.pivot_center ||
            isNaN(startDegree) ||
            isNaN(centerDegree) ||
            isNaN(stopDegree)
        )
            return;
        const slices = makeSliceGeometry(
            startDegree,
            centerDegree,
            stopDegree,
            irrigationSystem.pivot_center,
            irrigationSystem.pivot_length + irrigationSystem.end_gun_range,
        );
        const rxPolygon =
            rxGeometry.features.length > 1
                ? turf.union(rxGeometry)
                : rxGeometry.features[0];
        const intersections = rxPolygon
            ? slices.map((slice) => {
                  const intersection = turf.intersect(
                      turf.featureCollection([slice, rxPolygon]),
                  );
                  if (!intersection) return slice;
                  return intersection.geometry.type === "MultiPolygon"
                      ? intersection
                      : turf.multiPolygon([intersection.geometry.coordinates]);
              })
            : slices;
        intersections[0].properties = {
            plot_type: firstPlotType,
        };
        intersections[1].properties = {
            plot_type:
                firstPlotType === PlotType.Canary
                    ? PlotType.Reference
                    : PlotType.Canary,
        };
        form.setValue(
            "canary_geometry",
            firstPlotType === PlotType.Canary
                ? intersections[0].geometry
                : intersections[1].geometry,
            { shouldValidate: true },
        );
        form.setValue(
            "reference_geometry",
            firstPlotType === PlotType.Canary
                ? intersections[1].geometry
                : intersections[0].geometry,
            { shouldValidate: true },
        );
        addUpdateGeojsonSource(map, PLOT_HIGHLIGHT_SOURCE, {
            type: "geojson",
            data: {
                type: "FeatureCollection",
                features: intersections,
            },
        });
        addUpdateGeojsonLayers(map, [
            PLOT_HIGHLIGHT_FILL_LAYER,
            PLOT_HIGHLIGHT_LINE_LAYER,
        ]);
        moveLayersToTop(map, [
            PLOT_HIGHLIGHT_FILL_LAYER.id,
            PLOT_HIGHLIGHT_LINE_LAYER.id,
        ]);
        if (placement === "manual") {
            map.on("mousedown", PLOT_HIGHLIGHT_FILL_LAYER.id, startDrag);
            map.on("mousemove", drag);
            map.on("mouseup", stopDrag);
        }
        if (onFormChange) {
            const formData =
                placement === "automatic"
                    ? form.getValues()
                    : [
                          {
                              id: form.getValues("canary_id"),
                              geometry: form.getValues("canary_geometry"),
                              properties: {
                                  start_degree:
                                      firstPlotType === PlotType.Canary
                                          ? startDegree
                                          : centerDegree,
                                  stop_degree:
                                      firstPlotType === PlotType.Canary
                                          ? centerDegree
                                          : stopDegree,
                                  rate_offset: form.getValues("canary_offset"),
                                  plot_type: PlotType.Canary,
                              },
                          },
                          {
                              id: form.getValues("reference_id"),
                              geometry: form.getValues("reference_geometry"),
                              properties: {
                                  start_degree:
                                      firstPlotType === PlotType.Reference
                                          ? startDegree
                                          : centerDegree,
                                  stop_degree:
                                      firstPlotType === PlotType.Reference
                                          ? centerDegree
                                          : stopDegree,
                                  rate_offset:
                                      form.getValues("reference_offset"),
                                  plot_type: PlotType.Reference,
                              },
                          },
                      ];
            onFormChange(formData);
        }
    }, [
        map,
        mapLoaded,
        irrigationSystem,
        startDegree,
        centerDegree,
        stopDegree,
        sliceSize,
        canaryOffset,
        referenceOffset,
        firstPlotType,
        placement,
        rxGeometry,
        form,
        onFormChange,
        drag,
        startDrag,
        stopDrag,
        makeSliceGeometry,
    ]);
    useEffect(() => {
        return () => {
            if (map) {
                removeLayersIfExists(map, [
                    PLOT_HIGHLIGHT_FILL_LAYER.id,
                    PLOT_HIGHLIGHT_LINE_LAYER.id,
                ]);
                removeSourcesIfExists(map, [PLOT_HIGHLIGHT_SOURCE]);
                if (placement === "manual") {
                    map.off(
                        "mousedown",
                        PLOT_HIGHLIGHT_FILL_LAYER.id,
                        startDrag,
                    );
                    map.off("mousemove", drag);
                    map.off("mouseup", stopDrag);
                }
            }
        };
    }, [map, placement, startDrag, drag, stopDrag]);

    return (
        <FormProvider {...form}>
            <form className="flex flex-col w-full gap-2 text-sm px-2">
                {placement === "manual" && (
                    <div className="flex flex-row gap-2 items-baseline">
                        <NumberInput
                            name="start_degree"
                            label={t("start", { ns: "rx" })}
                            labelPlacement="outside"
                            isRequired
                            min={0}
                            max={360}
                            endContent={
                                <span className="text-gray-500">°</span>
                            }
                            onValueChange={(value) => {
                                const numberValue = Number(value);
                                form.setValue(
                                    "center_degree",
                                    incrementDegree(numberValue, sliceSize),
                                );
                                form.setValue(
                                    "stop_degree",
                                    incrementDegree(numberValue, sliceSize * 2),
                                );
                            }}
                        />
                        <NumberInput
                            name="center_degree"
                            label={t("center", { ns: "rx" })}
                            labelPlacement="outside"
                            isRequired
                            min={0}
                            max={360}
                            endContent={
                                <span className="text-gray-500">°</span>
                            }
                            onValueChange={(value) => {
                                if (value === "") return;
                                const numberValue = Number(value);
                                form.setValue(
                                    "start_degree",
                                    decrementDegree(numberValue, sliceSize),
                                );
                                form.setValue(
                                    "stop_degree",
                                    incrementDegree(numberValue, sliceSize),
                                );
                            }}
                        />
                        <NumberInput
                            name="stop_degree"
                            label={t("stop", { ns: "rx" })}
                            labelPlacement="outside"
                            isRequired
                            min={0}
                            max={360}
                            endContent={
                                <span className="text-gray-500">°</span>
                            }
                            onValueChange={(value) => {
                                if (value === "") return;
                                const numberValue = Number(value);
                                form.setValue(
                                    "start_degree",
                                    decrementDegree(numberValue, sliceSize * 2),
                                );
                                form.setValue(
                                    "center_degree",
                                    decrementDegree(numberValue, sliceSize),
                                );
                            }}
                        />
                        <Button
                            isIconOnly
                            variant="flat"
                            color="primary"
                            onPress={() => {
                                if (
                                    !startDegree ||
                                    !centerDegree ||
                                    !stopDegree
                                )
                                    return;
                                form.setValue(
                                    "start_degree",
                                    decrementDegree(startDegree),
                                );
                                form.setValue(
                                    "center_degree",
                                    decrementDegree(centerDegree),
                                );
                                form.setValue(
                                    "stop_degree",
                                    decrementDegree(stopDegree),
                                );
                            }}
                        >
                            <FontAwesomeIcon icon={faRotateLeft} />
                        </Button>
                        <Button
                            isIconOnly
                            variant="flat"
                            color="primary"
                            onPress={() => {
                                form.setValue(
                                    "start_degree",
                                    incrementDegree(startDegree),
                                );
                                form.setValue(
                                    "center_degree",
                                    incrementDegree(centerDegree),
                                );
                                form.setValue(
                                    "stop_degree",
                                    incrementDegree(stopDegree),
                                );
                            }}
                        >
                            <FontAwesomeIcon icon={faRotateRight} />
                        </Button>
                    </div>
                )}
                <div className="flex flex-row gap-2 items-center">
                    <NumberInput
                        name="slice_size"
                        label={t("slice_size", { ns: "rx" })}
                        labelPlacement="outside"
                        isRequired
                        min={5}
                        onValueChange={(value) => {
                            if (value === "" || !centerDegree) return;
                            form.setValue(
                                "start_degree",
                                decrementDegree(centerDegree, Number(value)),
                            );
                            form.setValue(
                                "stop_degree",
                                incrementDegree(centerDegree, Number(value)),
                            );
                        }}
                        endContent={
                            <div className="flex flex-row gap-1">
                                <span className="text-xs text-gray-500">°</span>
                                <Button
                                    isIconOnly
                                    size="sm"
                                    variant="flat"
                                    color="primary"
                                    onPress={() => {
                                        const currentValue = sliceSize;
                                        if (!currentValue || !centerDegree)
                                            return;
                                        form.setValue(
                                            "slice_size",
                                            currentValue - 1,
                                        );
                                        form.setValue(
                                            "start_degree",
                                            decrementDegree(
                                                centerDegree,
                                                currentValue - 1,
                                            ),
                                        );
                                        form.setValue(
                                            "stop_degree",
                                            incrementDegree(
                                                centerDegree,
                                                currentValue - 1,
                                            ),
                                        );
                                    }}
                                >
                                    <FontAwesomeIcon icon={faMinus} />
                                </Button>
                                <Button
                                    isIconOnly
                                    size="sm"
                                    variant="flat"
                                    color="primary"
                                    onPress={() => {
                                        const currentValue = sliceSize;
                                        if (!currentValue || !centerDegree)
                                            return;
                                        form.setValue(
                                            "slice_size",
                                            currentValue + 1,
                                        );
                                        form.setValue(
                                            "start_degree",
                                            decrementDegree(
                                                centerDegree,
                                                currentValue + 1,
                                            ),
                                        );
                                        form.setValue(
                                            "stop_degree",
                                            incrementDegree(
                                                centerDegree,
                                                currentValue + 1,
                                            ),
                                        );
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                </Button>
                            </div>
                        }
                    />
                    {placement === "manual" && (
                        <div className="flex flex-col">
                            <label>{t("plot_location", { ns: "rx" })}</label>
                            <Button
                                variant="flat"
                                color="primary"
                                onPress={() => {
                                    setFirstPlotType((prev) =>
                                        prev === PlotType.Canary
                                            ? PlotType.Reference
                                            : PlotType.Canary,
                                    );
                                }}
                            >
                                <FontAwesomeIcon icon={faShuffle} />
                                {t("swap", { ns: "rx" })}
                            </Button>
                        </div>
                    )}
                </div>
                <div className="flex flex-row w-full gap-2 justify-between items-end">
                    <NumberInput
                        name="canary_offset"
                        label={t("canary_offset", { ns: "rx" })}
                        labelPlacement="outside"
                        isRequired
                        min={-90}
                        max={0}
                        color="danger"
                        type="number"
                    />
                    <NumberInput
                        name="reference_offset"
                        label={t("reference_offset", { ns: "rx" })}
                        labelPlacement="outside"
                        isRequired
                        min={0}
                        max={90}
                        color="secondary"
                        type="number"
                    />
                </div>
            </form>
        </FormProvider>
    );
}
export default SlicePlotForm;
